import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchClients } from '../../../store/thunks/clientsThunk';
import { Client } from '../../../types';
import {
  exportClientsToExcel,
  fetchAllClients,
} from '../../../utils/exportClientsToExcel';

type SortField = 'userName' | 'createdAt';
type SortDir = 'ASC' | 'DESC';

const useCustomerLogic = () => {
  const { jwtToken } = useSelector((state: RootState) => state.auth);
  const loading = useSelector((state: RootState) => state.clients.loading);
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  // За замовчуванням сортуємо за датою реєстрації, від нових до старих.
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('DESC');

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [searchResults, setSearchResults] = useState<Client[]>([]); // Стан для результатів пошуку
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const currentPageIndex = useRef<number>(1);
  const observerTarget = useRef<HTMLDivElement | null>(null);
  const initialLoad = useRef<boolean>(true);
  const isInitialRequestDone = useRef<boolean>(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Візуальні індикатори сортування для шапки списку.
  const sortingLogo = sortField === 'userName' && sortDir === 'DESC'; // Я→А
  const isRotated = sortField === 'createdAt' && sortDir === 'ASC'; // старі зверху

  // Завантаження клієнтів
  const loadClients = useCallback(
    async (page: number, field: SortField, dir: SortDir) => {
      if (loading || isLoadingMore || isSearching) return;

      setIsLoadingMore(true);
      try {
        const result = await dispatch(fetchClients(page, 12, dir, '', field));
        const clients: Client[] = result.clients;

        if (Array.isArray(clients) && clients.length > 0) {
          setAllClients((prevClients) =>
            page === 1 ? clients : [...prevClients, ...clients],
          );
          setHasNextPage(clients.length === 12);
          currentPageIndex.current = page;
        } else {
          setHasNextPage(false);
        }
      } catch (error) {
        console.error('Failed to load clients:', error);
      } finally {
        setIsLoadingMore(false);
        if (page === 1) {
          initialLoad.current = false;
          isInitialRequestDone.current = true;
        }
      }
    },
    [dispatch, loading, isLoadingMore, isSearching],
  );

  useEffect(() => {
    if (!jwtToken) {
      navigate('/');
    } else if (!isInitialRequestDone.current) {
      isInitialRequestDone.current = true;
      loadClients(1, sortField, sortDir);
    }
  }, [jwtToken, navigate, sortField, sortDir, loadClients]);

  // Завантаження більше клієнтів (для пагінації)
  const loadMoreClients = useCallback(() => {
    if (hasNextPage && !loading && !isSearching && !isLoadingMore) {
      const nextPage = currentPageIndex.current + 1;
      loadClients(nextPage, sortField, sortDir);
    }
  }, [
    hasNextPage,
    loading,
    isSearching,
    isLoadingMore,
    sortField,
    sortDir,
    loadClients,
  ]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          loadMoreClients();
        }
      },
      {
        root: null,
        rootMargin: '20px',
        threshold: 1.0,
      },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasNextPage, loadMoreClients]);

  // Логіка для пошуку клієнтів
  const debouncedSearch = useCallback(
    (query: string) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      if (query.length >= 2) {
        debounceTimeout.current = setTimeout(() => {
          setIsSearching(true);
          dispatch(fetchClients(1, 60, sortDir, query, sortField))
            .then((results: any) => {
              const clients: Client[] = results.clients;

              setSearchResults(clients); // Оновлюємо результати пошуку
              setHasNextPage(clients.length === 60);
              setIsSearching(false);
            })
            .catch((error) => {
              console.error('Search failed:', error);
              setIsSearching(false);
            });
        }, 500); // Затримка debounce
      } else {
        // Якщо пошуковий запит короткий, скидаємо результати пошуку
        setSearchResults([]);
        setIsSearching(false);
      }
    },
    [dispatch, sortField, sortDir],
  );

  useEffect(() => {
    if (searchQuery) {
      debouncedSearch(searchQuery);
    }
  }, [searchQuery, debouncedSearch]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const applySort = (field: SortField, dir: SortDir) => {
    setSortField(field);
    setSortDir(dir);
    currentPageIndex.current = 1;
    setAllClients([]);
    loadClients(1, field, dir);
  };

  // Кнопка сортування за іменем (А→Я / Я→А).
  const toggleSortingDirection = () => {
    const dir: SortDir =
      sortField === 'userName' ? (sortDir === 'ASC' ? 'DESC' : 'ASC') : 'ASC';
    applySort('userName', dir);
  };

  // Стрілка сортування за датою реєстрації (нові↔старі).
  const handleSortArrowClick = () => {
    const dir: SortDir =
      sortField === 'createdAt' ? (sortDir === 'DESC' ? 'ASC' : 'DESC') : 'DESC';
    applySort('createdAt', dir);
  };

  // Вивантаження у Excel того, що зараз показано (з урахуванням пошуку/сортування).
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const clients = await fetchAllClients(sortDir, searchQuery, sortField);
      exportClientsToExcel(clients);
    } catch (error) {
      console.error('Не вдалося вивантажити Excel:', error);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, sortDir, searchQuery, sortField]);

  // Локальне оновлення коментаря клієнта у списку (без повного перезавантаження).
  const updateClientCommentLocal = useCallback(
    (clientId: string, comment: string) => {
      const patch = (list: Client[]) =>
        list.map((c) => (c.id === clientId ? { ...c, comment } : c));
      setAllClients((prev) => patch(prev));
      setSearchResults((prev) => patch(prev));
    },
    [],
  );

  return {
    sortingLogo,
    isRotated,
    allClients,
    searchResults,
    observerTarget,
    isSearching,
    isExporting,
    handleSortArrowClick,
    toggleSortingDirection,
    handleSearchChange,
    handleExportExcel,
    updateClientCommentLocal,
    setIsSearching,
  };
};

export default useCustomerLogic;
